import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../flux/stores/hooks';
import { startApsConnect, fetchApsConnection } from '../flux/actions/apsThunks';
import { useApiAccessToken } from './useApiAccessToken';
import { API_BASE_URL } from '../apiConfig';

// The callback page posts its result from the API's origin, not the app's.
const API_ORIGIN = new URL(API_BASE_URL, window.location.origin).origin;

const POPUP_FEATURES = 'width=620,height=760,menubar=no,toolbar=no';
const CLOSED_POLL_MS = 500;

/**
 * Runs the Autodesk consent flow in a popup and refreshes the project's
 * connection when it comes back.
 *
 * The flow ends with our own callback page posting `aps-connected` or
 * `aps-error` to `window.opener` and closing itself. This hook is the other
 * half of that conversation.
 *
 * Note that `REACT_APP_API_BASE_URL` must point at the same origin the backend
 * has configured as its `frontend_base_url` counterpart. If the two disagree
 * the message is silently dropped by the browser, and the flow appears to hang
 * with the popup closing and nothing happening.
 */
export function useApsConnectPopup(projectId) {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    const popupRef = useRef(null);
    const pollRef = useRef(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);

    const stopWatching = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        popupRef.current = null;
        setIsConnecting(false);
    }, []);

    const refreshConnection = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            if (accessToken) dispatch(fetchApsConnection({ accessToken, projectId }));
        } catch (tokenError) {
            console.error('Failed to acquire API token after the Autodesk connect:', tokenError);
        }
    }, [dispatch, getAccessToken, projectId]);

    useEffect(() => {
        function handleMessage(event) {
            // Two checks, because either alone is too weak: the origin proves
            // the message came from our backend, and the source proves it came
            // from the window this hook opened rather than another tab's flow.
            if (event.origin !== API_ORIGIN) return;
            if (popupRef.current && event.source && event.source !== popupRef.current) return;

            const type = event.data && event.data.type;
            if (type !== 'aps-connected' && type !== 'aps-error') return;

            stopWatching();
            if (type === 'aps-error') {
                setError('Autodesk did not complete the connection. Please try again.');
                return;
            }
            setError(null);
            refreshConnection();
        }

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [refreshConnection, stopWatching]);

    // Clear the timer on unmount, but leave the popup open: the user may be
    // part-way through signing in to Autodesk, and closing it would discard
    // their work for no reason.
    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    const connect = useCallback(async () => {
        setError(null);

        // Opened before the await, and deliberately so. Browsers only allow
        // window.open during a user gesture, and awaiting the authorize URL
        // first would put the call outside that window and get it blocked. The
        // popup starts blank and is pointed at Autodesk once the URL arrives.
        const popup = window.open('', 'bimiq-aps-connect', POPUP_FEATURES);
        if (!popup) {
            setError('Your browser blocked the Autodesk window. Allow pop-ups for this site, then try again.');
            return;
        }
        popupRef.current = popup;
        setIsConnecting(true);

        const fail = (message) => {
            popup.close();
            stopWatching();
            setError(message);
        };

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) {
                // A redirect for interactive auth is already under way.
                popup.close();
                stopWatching();
                return;
            }

            const result = await dispatch(startApsConnect({ accessToken, projectId }));
            if (!startApsConnect.fulfilled.match(result)) {
                fail(result.payload?.message ?? 'Could not start the Autodesk connection.');
                return;
            }
            popup.location.href = result.payload.authorizeUrl;
        } catch (startError) {
            console.error('Failed to start the Autodesk connection:', startError);
            fail('Could not start the Autodesk connection.');
            return;
        }

        // Closing the window is a silent cancel: no message is ever posted, so
        // without this the button would stay disabled until a reload.
        pollRef.current = setInterval(() => {
            if (popupRef.current && popupRef.current.closed) stopWatching();
        }, CLOSED_POLL_MS);
    }, [dispatch, getAccessToken, projectId, stopWatching]);

    return { connect, isConnecting, error, clearError: () => setError(null) };
}
